import { useState, useEffect } from 'react';
import { 
    Plus, 
    DollarSign,
    TrendingUp,
    TrendingDown,
    FileText,
    CheckCircle,
    Clock,
    AlertCircle,
    Download,
    Printer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { generateInvoicePDF } from '../utils/pdfGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FinancialsPage() {
    const [invoices, setInvoices] = useState([]);
    const [loads, setLoads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);

    const [invoiceForm, setInvoiceForm] = useState({
        load_id: '',
        amount: '',
        due_date: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

            const [invoicesRes, loadsRes, statsRes] = await Promise.all([
                fetch(`${API_URL}/api/invoices?${params}`, { credentials: 'include' }),
                fetch(`${API_URL}/api/loads`, { credentials: 'include' }),
                fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' })
            ]);

            if (invoicesRes.ok) {
                const invoicesData = await invoicesRes.json();
                setInvoices(invoicesData);
            }
            if (loadsRes.ok) {
                const loadsData = await loadsRes.json();
                setLoads(loadsData);
            }
            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to fetch financial data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateInvoice = async () => {
        if (!invoiceForm.load_id || !invoiceForm.amount || !invoiceForm.due_date) {
            toast.error('Please fill in all required fields');
            return;
        }

        setCreating(true);
        try {
            const response = await fetch(`${API_URL}/api/invoices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    load_id: invoiceForm.load_id,
                    amount: parseFloat(invoiceForm.amount),
                    due_date: invoiceForm.due_date,
                    notes: invoiceForm.notes || null
                })
            });

            if (response.ok) {
                toast.success('Invoice created successfully!');
                setShowCreateDialog(false);
                setInvoiceForm({
                    load_id: '',
                    amount: '',
                    due_date: '',
                    notes: ''
                });
                fetchData();
            } else {
                toast.error('Failed to create invoice');
            }
        } catch (error) {
            console.error('Failed to create invoice:', error);
            toast.error('Failed to create invoice');
        } finally {
            setCreating(false);
        }
    };

    const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
        try {
            const updateData = { status: newStatus };
            if (newStatus === 'paid') {
                updateData.paid_date = new Date().toISOString().split('T')[0];
            }

            const response = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                toast.success(`Invoice marked as ${newStatus}`);
                fetchData();
            } else {
                toast.error('Failed to update invoice');
            }
        } catch (error) {
            console.error('Failed to update invoice:', error);
            toast.error('Failed to update invoice');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'status-pending',
            paid: 'status-active',
            overdue: 'status-delayed',
            cancelled: 'bg-zinc-700 text-zinc-400'
        };
        return styles[status] || 'status-pending';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const totalPending = invoices
        .filter(i => i.status === 'pending')
        .reduce((sum, i) => sum + (i.amount || 0), 0);

    const totalPaid = invoices
        .filter(i => i.status === 'paid')
        .reduce((sum, i) => sum + (i.amount || 0), 0);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="financials-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Financials</h1>
                    <p className="text-zinc-500 text-sm mt-1">100% Gross Rate from Rate Con — Broker billing & invoice tracking</p>
                </div>
                <Button 
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="create-invoice-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    New Invoice
                </Button>
            </div>

            {/* Gross Billing Banner */}
            <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 border-green-500/30">
                <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <DollarSign className="w-8 h-8 text-green-400" />
                            <div>
                                <h2 className="text-lg font-bold text-white">Broker Billing (100% Gross)</h2>
                                <p className="text-zinc-400 text-sm">Full Rate Con amount billed to brokers</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-zinc-500">Total Billable</p>
                            <p className="text-3xl font-bold font-mono text-green-400">
                                {formatCurrency(stats?.revenue?.total)}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Financial Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                                    Total Gross Revenue
                                </p>
                                <p className="text-3xl font-bold text-white font-mono">
                                    {formatCurrency(stats?.revenue?.total)}
                                </p>
                                <p className="text-sm text-zinc-500 mt-1">All delivered loads (100%)</p>
                            </div>
                            <div className="p-2 rounded-sm bg-green-500/20 text-green-500">
                                <TrendingUp className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                                    Pending Gross
                                </p>
                                <p className="text-3xl font-bold text-yellow-400 font-mono">
                                    {formatCurrency(stats?.revenue?.pending)}
                                </p>
                                <p className="text-sm text-zinc-500 mt-1">Booked & in transit</p>
                            </div>
                            <div className="p-2 rounded-sm bg-yellow-500/20 text-yellow-500">
                                <Clock className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                                    Invoices Paid
                                </p>
                                <p className="text-3xl font-bold text-green-400 font-mono">
                                    {formatCurrency(totalPaid)}
                                </p>
                                <p className="text-sm text-zinc-500 mt-1">
                                    {invoices.filter(i => i.status === 'paid').length} invoices
                                </p>
                            </div>
                            <div className="p-2 rounded-sm bg-green-500/20 text-green-500">
                                <CheckCircle className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                                    Invoices Pending
                                </p>
                                <p className="text-3xl font-bold text-orange-400 font-mono">
                                    {formatCurrency(totalPending)}
                                </p>
                                <p className="text-sm text-zinc-500 mt-1">
                                    {invoices.filter(i => i.status === 'pending').length} invoices
                                </p>
                            </div>
                            <div className="p-2 rounded-sm bg-orange-500/20 text-orange-500">
                                <FileText className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filter */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <Select
                            value={statusFilter}
                            onValueChange={setStatusFilter}
                        >
                            <SelectTrigger className="w-full md:w-48 bg-zinc-900 border-zinc-800" data-testid="status-filter">
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="overdue">Overdue</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex-1" />
                        <Button variant="outline" className="border-zinc-700" data-testid="export-btn">
                            <Download className="w-4 h-4 mr-2" />
                            Export
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="invoices-table-card">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                            Invoices
                        </CardTitle>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                            {invoices.length} total
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="empty-state py-12">
                            <FileText className="w-12 h-12 text-zinc-700 mb-4" />
                            <p className="text-zinc-500">No invoices found</p>
                            <Button 
                                variant="outline" 
                                className="mt-4 border-zinc-700"
                                onClick={() => setShowCreateDialog(true)}
                            >
                                Create Your First Invoice
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Invoice ID</th>
                                        <th>Load ID</th>
                                        <th>Amount</th>
                                        <th>Due Date</th>
                                        <th>Paid Date</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice) => (
                                        <tr key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`}>
                                            <td className="text-orange-400">{invoice.invoice_id}</td>
                                            <td className="text-white">{invoice.load_id}</td>
                                            <td className="text-green-400 font-semibold">{formatCurrency(invoice.amount)}</td>
                                            <td className="text-zinc-400">{formatDate(invoice.due_date)}</td>
                                            <td className="text-zinc-400">{formatDate(invoice.paid_date)}</td>
                                            <td>
                                                <Badge className={`${getStatusBadge(invoice.status)} text-xs uppercase`}>
                                                    {invoice.status}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    {invoice.status === 'pending' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-green-700 text-green-400 hover:bg-green-500/10 text-xs"
                                                            onClick={() => handleUpdateInvoiceStatus(invoice.invoice_id, 'paid')}
                                                            data-testid={`mark-paid-btn-${invoice.invoice_id}`}
                                                        >
                                                            Mark Paid
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs"
                                                        onClick={() => {
                                                            try {
                                                                const load = loads.find(l => l.load_id === invoice.load_id);
                                                                if (!load) {
                                                                    console.warn('Load not found for invoice:', invoice.load_id);
                                                                }
                                                                generateInvoicePDF(invoice, load);
                                                                toast.success('Invoice PDF downloaded!');
                                                            } catch (error) {
                                                                console.error('PDF generation error:', error);
                                                                toast.error('Failed to generate PDF: ' + error.message);
                                                            }
                                                        }}
                                                        data-testid={`download-invoice-btn-${invoice.invoice_id}`}
                                                    >
                                                        <Printer className="w-3 h-3 mr-1" />
                                                        PDF
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create Invoice Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create Invoice</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Generate a new invoice for a load
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-zinc-400">Load *</Label>
                            <Select
                                value={invoiceForm.load_id}
                                onValueChange={(value) => {
                                    const selectedLoad = loads.find(l => l.load_id === value);
                                    setInvoiceForm({ 
                                        ...invoiceForm, 
                                        load_id: value,
                                        amount: selectedLoad?.rate?.toString() || ''
                                    });
                                }}
                            >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="invoice-load-select">
                                    <SelectValue placeholder="Select a load" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                                    {loads.map(load => (
                                        <SelectItem key={load.load_id} value={load.load_id}>
                                            {load.load_id} - {load.origin_city} → {load.destination_city}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-zinc-400">Amount *</Label>
                            <div className="relative mt-1">
                                <DollarSign className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input
                                    type="number"
                                    value={invoiceForm.amount}
                                    onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 pl-9"
                                    placeholder="3500"
                                    data-testid="invoice-amount-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-zinc-400">Due Date *</Label>
                            <Input
                                type="date"
                                value={invoiceForm.due_date}
                                onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                data-testid="invoice-due-date-input"
                            />
                        </div>
                        <div>
                            <Label className="text-zinc-400">Notes</Label>
                            <Input
                                value={invoiceForm.notes}
                                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowCreateDialog(false)}
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateInvoice}
                            disabled={creating}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="submit-invoice-btn"
                        >
                            {creating ? 'Creating...' : 'Create Invoice'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
